import { Link } from 'react-router-dom'
import {
  BookOpen, Boxes, Calculator, Check, Clock, CreditCard, FileText, Grid3x3,
  HandCoins, Hammer, Landmark, Layers, Package, Receipt, Ruler, ScanBarcode, ShoppingCart, Sparkles, Truck, Users,
} from 'lucide-react'
import '../plancards.css'

// The plans as cards: a switch for the line of business (Invoice, Store,
// Contract), then its three tiers. Used on the landing page's pricing and in
// Settings → Plan & billing. Prices and what each plan includes come from the
// server (modules.PLANS); the words and icons for each plan live here.

/** Who each line is for. */
export const LINE_COPY = {
  invoice: 'For service businesses, freelancers and traders who bill on invoice.',
  store: 'For shops, boutiques and mini-marts that sell at the counter.',
  contract: 'For contractors and made-to-order businesses: trades, tailors and fashion designers, furniture makers, printers.',
}

/** Each plan's headline, one line about it, and what it brings. */
export const PLAN_COPY = {
  invoice_basic: {
    headline: 'Get paid for your work',
    description: 'Quotes and invoices, with every payment and what each customer owes in one place.',
    features: [
      [FileText, 'Quotes and invoices'],
      [HandCoins, 'Customer payments and credit memos'],
      [Clock, 'What each customer owes, and aging'],
      [Receipt, 'Vendor bills for services and subcontractors'],
    ],
  },
  invoice_advanced: {
    headline: 'Stock and books too',
    description: 'Everything in Basic, with the goods you buy and sell, and full accounting.',
    features: [
      [Check, 'Everything in Basic'],
      [ShoppingCart, 'Purchase orders, goods received and product bills'],
      [Package, 'Inventory by location, with FIFO cost'],
      [BookOpen, 'Accounting: statements, ledgers and month-end close'],
      [Landmark, 'Bank reconciliation, fixed assets and prepaids'],
    ],
  },
  store_basic: {
    headline: 'Sell, stock and restock',
    description: 'Point of sale, purchasing and stock for shops, boutiques and mini-marts.',
    features: [
      [CreditCard, 'Point of sale: cash, card and USSD'],
      [ShoppingCart, 'Purchase orders, goods received and bills'],
      [Package, 'Inventory by location, with FIFO cost'],
      [ScanBarcode, 'Stock counts, barcodes and labels'],
    ],
  },
  store_advanced: {
    headline: 'Every shelf counted',
    description: 'Everything in Basic, with stock by bin and full accounting.',
    features: [
      [Check, 'Everything in Basic'],
      [Grid3x3, 'Bin tracking, shelf by shelf'],
      [BookOpen, 'Accounting: statements, ledgers and month-end close'],
      [Landmark, 'Bank reconciliation, fixed assets and prepaids'],
    ],
  },
  contract_basic: {
    headline: 'Run your jobs and orders',
    description: 'Quote, bill and track every job or custom order, with its materials and outside work.',
    features: [
      [Hammer, 'Jobs with profit and margin'],
      [FileText, 'Quotes and invoices made from a job'],
      [Layers, 'Deposits, billing in stages, change orders and retainage'],
      [Truck, 'Purchase orders and bills for materials and subcontractors'],
      [Ruler, 'Customer measurements for tailors and fashion designers'],
    ],
  },
  contract_advanced: {
    headline: 'Profit on every job',
    description: 'Everything in Basic, with full accounting and income on long jobs as the work is done.',
    features: [
      [Check, 'Everything in Basic'],
      [Calculator, 'Income on long jobs as the work is done'],
      [FileText, 'Quotes and invoices not from a job'],
      [BookOpen, 'Accounting: statements, ledgers and month-end close'],
      [Landmark, 'Bank reconciliation, fixed assets and prepaids'],
    ],
  },
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

/** Invoice | Store | Contract, as a segmented switch. */
export function LineSwitch({ lines = [], value, onChange }) {
  return (
    <div className="pc-switch" role="group" aria-label="Kind of business">
      {lines.map((l) => (
        <button key={l.id} type="button" aria-pressed={value === l.id} onClick={() => onChange(l.id)}>{l.label}</button>
      ))}
    </div>
  )
}

/**
 * The three tiers of one line. `action(plan)` says what each card's button
 * does: { label, to } for a link, { label, onClick } for a button, and
 * `disabled` or `primary` to change its look.
 */
export default function PlanCards({ data, line, action, selected }) {
  const plans = (data?.plans || []).filter((p) => p.line === line)
  return (
    <div className="pc-cards">
      {plans.map((p) => {
        const copy = PLAN_COPY[p.id] || {}
        const featured = p.tier === 'advanced'
        const a = action(p)
        const cls = `pc-btn${a.disabled ? ' pc-btn-quiet' : a.primary ? ' pc-btn-primary' : ''}`
        return (
          <article key={p.id} className={`pc-card${featured ? ' pc-featured' : ''}${selected === p.id ? ' pc-selected' : ''}`}>
            <div className="pc-card-top">
              <h3>{p.label.replace(`${data.lines.find((l) => l.id === line)?.label} `, '')}</h3>
              {featured && <span className="pc-flag">Books included</span>}
            </div>
            <p className="pc-headline">{copy.headline}</p>
            <p className="pc-description">{copy.description}</p>
            {a.to
              ? <Link className={cls} to={a.to}>{a.primary && <Sparkles size={16} aria-hidden="true" />}{a.label}</Link>
              : <button type="button" className={cls} onClick={a.onClick} disabled={a.disabled}>{a.primary && !a.disabled && <Sparkles size={16} aria-hidden="true" />}{a.label}</button>}
            <p className="pc-price"><sup>$</sup>{Number(p.price).toLocaleString()}<span>/ month</span></p>
            <ul className="pc-features">
              {(copy.features || []).map(([Icon, text]) => <li key={text}><Icon size={20} aria-hidden="true" />{text}</li>)}
              <li><Users size={20} aria-hidden="true" />{p.users} users, {p.locations} location{p.locations === 1 ? '' : 's'}; cashiers free</li>
              <li><Receipt size={20} aria-hidden="true" />Expenses with receipts</li>
            </ul>
            {p.addons.length > 0 && (
              <p className="pc-addons">
                <Boxes size={14} aria-hidden="true" /> Add-ons: {p.addons.map((x) => `${x.label} +${money(x.price)}`).join(' · ')}
              </p>
            )}
          </article>
        )
      })}
    </div>
  )
}
